import type {ReactNode} from 'react';
import type {Dictionary} from '@wix/devzai-utils-common';

/**
 * Render props passed to custom control render functions.
 */
export interface DoppeViewerSettingControlCustomRenderProps<VALUE, FORM_DATA extends Dictionary = Dictionary> {
    /**
     * Current value of the field.
     */
    value: VALUE;

    /**
     * Callback to update the field value.
     */
    onValueChange: (value: VALUE) => void;

    /**
     * Whether the field has a validation error.
     */
    hasError: boolean;

    /**
     * Callback to trigger on input blur (starts error reporting for the field).
     */
    onBlur: () => void;

    /**
     * Whether the field is disabled.
     */
    disabled?: boolean;

    /**
     * HTML ID for the input element (for label association).
     */
    inputId: string;

    /**
     * The current form data (all field values).
     * Useful for controls that need to adjust based on other field values.
     */
    formData: FORM_DATA;
}

/**
 * A custom setting control for viewer forms.
 * Only 'custom' control type is supported in viewer forms.
 */
export interface DoppeViewerSettingControlCustom<VALUE> {
    controlType: 'custom';
    render: (props: DoppeViewerSettingControlCustomRenderProps<VALUE>) => ReactNode;
}

/**
 * Factory function to create a custom setting control.
 *
 * @example
 * ```tsx
 * const textControl = doppeViewerSettingControlCustom<string>({
 *     render: (props) => (
 *         <DoppeActionWidgetTextBox
 *             value={props.value}
 *             onTextChange={props.onValueChange}
 *             hasError={props.hasError}
 *             onBlur={props.onBlur}
 *         />
 *     )
 * });
 * ```
 */
export function doppeViewerSettingControlCustom<VALUE>(
    options: {
        render: DoppeViewerSettingControlCustomRenderProps<VALUE> extends infer R
            ? (props: R) => ReactNode
            : never;
    }
): DoppeViewerSettingControlCustom<VALUE> {
    return {
        controlType: 'custom',
        render: options.render as DoppeViewerSettingControlCustom<VALUE>['render'],
    };
}
